import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Check, ChevronDown } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLang } from "@/context/LanguageContext";

export const Header = () => {
    const { t, lang, setLang, locales } = useLang();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    const links = [
        { to: "/", label: t.nav.home, end: true, key: "home" },
        { to: "/about", label: t.nav.about, key: "about" },
        { to: "/services", label: t.nav.services, key: "services" },
        { to: "/contact", label: t.nav.contact, key: "contact" },
    ];

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-colors duration-200 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-xl border-b border-steel-100"
                    : "bg-white/40 backdrop-blur-md border-b border-transparent"
            }`}
        >
            <div className="mx-auto max-w-[1440px] px-5 lg:px-10 h-16 lg:h-20 flex items-center justify-between gap-4">
                <Link to="/" data-testid="brand-logo" className="flex items-center gap-2" aria-label="OTHRYX">
                    <span className="font-display text-navy text-xl lg:text-2xl tracking-tighter">
                        OTHRYX
                    </span>
                    <span className="hidden sm:inline-block font-mono-tech text-[10px] uppercase tracking-[0.3em] text-steel-600 border-s border-steel-200 ps-2 ms-1">
                        S.R.L.
                    </span>
                </Link>

                <nav className="hidden xl:flex items-center gap-6" aria-label="Primary">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={`nav-link-${l.key}`}
                            className={({ isActive }) =>
                                `font-mono-tech text-[11px] uppercase tracking-[0.18em] link-underline transition-colors ${
                                    isActive ? "text-navy" : "text-steel-600 hover:text-navy"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="language-switcher"
                                className="font-mono-tech text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-steel-200 hover:border-navy text-navy transition-colors inline-flex items-center gap-1.5"
                                aria-label="Switch language"
                            >
                                {locales.find((l) => l.code === lang)?.label}
                                <ChevronDown className="h-3 w-3" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-none border-steel-200 min-w-[160px] bg-white">
                            {locales.map((l) => (
                                <DropdownMenuItem
                                    key={l.code}
                                    data-testid={`lang-option-${l.code}`}
                                    onClick={() => setLang(l.code)}
                                    className="rounded-none font-mono-tech text-[12px] uppercase tracking-[0.15em] cursor-pointer flex items-center justify-between"
                                >
                                    <span>{l.name}</span>
                                    {l.code === lang && <Check className="h-3.5 w-3.5" />}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <Link
                        to="/contact"
                        data-testid="header-cta"
                        className="hidden md:inline-flex items-center font-mono-tech text-[11px] uppercase tracking-[0.2em] bg-navy text-white px-4 py-2.5 hover:bg-navy-light transition-colors"
                    >
                        {t.nav.cta}
                    </Link>

                    <button
                        className="xl:hidden p-2 -me-2 text-navy"
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="xl:hidden bg-white border-t border-steel-100 max-h-[calc(100vh-4rem)] overflow-y-auto">
                    <nav className="px-6 py-6 flex flex-col gap-4">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                data-testid={`mobile-nav-${l.key}`}
                                className={({ isActive }) =>
                                    `font-display text-2xl tracking-tight uppercase ${
                                        isActive ? "text-navy" : "text-steel-600"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="mobile-cta"
                            className="mt-2 inline-flex w-fit font-mono-tech text-[11px] uppercase tracking-[0.2em] bg-navy text-white px-4 py-3 hover:bg-navy-light transition-colors"
                        >
                            {t.nav.cta}
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
